import { morph, MORPH_OPTIONS } from '@theme/morph';
import { startViewTransition } from '@theme/utilities';

const pageCache = new Map();
const prefetches = new Map();
let navigationController;
let pendingLink;

function setPending(link, pending) {
  if (pendingLink && pendingLink !== link) {
    pendingLink.classList.remove('is-pending');
    pendingLink.removeAttribute('aria-busy');
  }

  pendingLink = pending ? link : undefined;
  link?.classList.toggle('is-pending', pending);

  if (pending) {
    link?.setAttribute('aria-busy', 'true');
  } else {
    link?.removeAttribute('aria-busy');
  }
}

function parsePage(html) {
  const page = new DOMParser().parseFromString(html, 'text/html');
  const main = page.querySelector('main');

  if (!main) throw new Error('Product page did not include a main element.');

  return { page, main };
}

async function fetchPage(url, signal) {
  if (pageCache.has(url)) return pageCache.get(url);

  if (prefetches.has(url)) {
    const prefetchedHtml = await prefetches.get(url);
    if (signal.aborted) throw new DOMException('Navigation cancelled', 'AbortError');
    if (prefetchedHtml) return prefetchedHtml;
  }

  const response = await fetch(url, {
    signal,
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
  });

  if (!response.ok) throw new Error(`Product page request failed with ${response.status}.`);

  const html = await response.text();
  pageCache.set(url, html);
  return html;
}

function prefetchPage(url) {
  if (pageCache.has(url) || prefetches.has(url)) return;

  const request = fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
    .then((response) => {
      if (!response.ok) return undefined;
      return response.text();
    })
    .then((html) => {
      if (html) pageCache.set(url, html);
      return html;
    })
    .catch(() => undefined)
    .finally(() => prefetches.delete(url));

  prefetches.set(url, request);
}

async function navigateToFlavor(url, link, { pushState = true, restoreFocus = false } = {}) {
  navigationController?.abort();
  const controller = new AbortController();
  navigationController = controller;
  setPending(link, true);

  try {
    const html = await fetchPage(url, controller.signal);
    if (controller.signal.aborted) return;

    const { page, main: newMain } = parsePage(html);
    const oldMain = document.querySelector('main');
    if (!oldMain) throw new Error('Current page did not include a main element.');

    await startViewTransition(() => {
      morph(oldMain, newMain, MORPH_OPTIONS);
    }, ['proda-flavor-crossfade']);

    document.title = page.title;

    if (pushState) history.pushState({}, '', url);
    document.dispatchEvent(new CustomEvent('flavor:changed'));

    if (restoreFocus) {
      requestAnimationFrame(() => {
        document.querySelector('.flavor-card--active')?.focus({ preventScroll: true });
      });
    }
  } catch (error) {
    if (error?.name !== 'AbortError') window.location.assign(url);
  } finally {
    if (navigationController === controller) navigationController = undefined;
    if (pendingLink === link) setPending(link, false);
  }
}

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;

  const link = event.target.closest('a.flavor-card');
  if (!link) return;

  if (link.classList.contains('flavor-card--active')) {
    event.preventDefault();
    return;
  }

  if (
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  ) {
    return;
  }

  event.preventDefault();
  navigateToFlavor(link.href, link, { restoreFocus: event.detail === 0 });
});

function handleIntent(event) {
  if (!(event.target instanceof Element)) return;

  const link = event.target.closest('a.flavor-card:not(.flavor-card--active)');
  if (link) prefetchPage(link.href);
}

document.addEventListener('pointerover', handleIntent, { passive: true });
document.addEventListener('focusin', handleIntent);

window.addEventListener('popstate', () => {
  navigateToFlavor(location.href, undefined, { pushState: false });
});
